import * as React from 'react';
import { DSAutocomplete } from '../Generic';
import { DSCheckbox, DSListItemText, DSMenuItem, DSTextField } from '../mui';

export type AutocompleteWithCheckboxValue<T> = {
    label: string;
    value: T;
};

export type AutocompleteWithCheckboxProps<T> = {
    values: Array<AutocompleteWithCheckboxValue<T>>;
    onChange: (newValues: Array<AutocompleteWithCheckboxValue<T>>) => void;
    disableClearable?: boolean;
    disableCloseOnSelect?: boolean;
    helperText?: string;
    label?: string;
    placeholder?: string;
    variant?: 'filled' | 'outlined' | 'standard';
};

export function AutocompleteWithCheckbox<T>({
    disableClearable,
    disableCloseOnSelect,
    helperText,
    label,
    onChange,
    placeholder,
    values,
    variant = 'standard',
}: AutocompleteWithCheckboxProps<T>): JSX.Element {
    const handleChange = React.useCallback(
        (_event: React.ChangeEvent<unknown> | null, newValue: Array<AutocompleteWithCheckboxValue<T>>) => {
            onChange(newValue);
        },
        [onChange],
    );

    return (
        <DSAutocomplete
            defaultValue={[]}
            disableClearable={disableClearable}
            disableCloseOnSelect={disableCloseOnSelect}
            getOptionLabel={option => option.label}
            multiple
            options={values}
            renderInput={params => (
                <DSTextField
                    {...params}
                    aria-label={label}
                    helperText={helperText}
                    label={label}
                    placeholder={placeholder}
                    variant={variant}
                />
            )}
            renderOption={(option, { selected }) => (
                <DSMenuItem key={option.label}>
                    <DSCheckbox checked={selected} />
                    <DSListItemText primary={option.label} />
                </DSMenuItem>
            )}
            selectOnFocus
            onChange={handleChange}
        />
    );
}

AutocompleteWithCheckbox.displayName = 'AutocompleteWithCheckbox';
